import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for managing payroll data (Admin view)
 * @param {string} businessId - Current business ID
 */
export const usePayroll = (businessId) => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!businessId) {
      setPayrolls([]);
      setEmployees([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const [payrollRes, empRes] = await Promise.all([
        api.get(`/payroll?business_id=${businessId}`),
        api.get(`/employees?business_id=${businessId}`)
      ]);
      setPayrolls(payrollRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch payroll data');
      console.error('Error fetching payroll:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createPayroll = async (payrollData) => {
    const res = await api.post('/payroll', { ...payrollData, business_id: businessId });
    await fetchData();
    return res.data;
  };

  const generateBulkPayroll = async (month, year, options = {}) => {
    const { enable_pf = true, enable_esi = true, enable_pt = false } = options;
    const params = new URLSearchParams({
      business_id: businessId,
      month,
      year,
      enable_pf,
      enable_esi,
      enable_pt,
    });
    const res = await api.post(`/payroll/generate-bulk?${params.toString()}`);
    await fetchData();
    return res.data;
  };

  const processPayroll = async (payrollId) => {
    const res = await api.put(`/payroll/${payrollId}/process`);
    await fetchData();
    return res.data;
  };

  const autoCalculate = async (params) => {
    const res = await api.post(`/payroll/auto-calculate?${new URLSearchParams(params).toString()}`);
    return res.data;
  };

  const manualCalculate = async (params) => {
    const res = await api.post(`/payroll/calculate?${new URLSearchParams(params).toString()}`);
    return res.data;
  };

  const fetchPendingAdvances = useCallback(async (employeeId) => {
    const res = await api.get(`/payroll/pending-advances/${employeeId}?business_id=${businessId}`);
    return res.data.advances || [];
  }, [businessId]);

  const fetchAttendanceSummary = useCallback(async (employeeId, month, year, grossSalary) => {
    const res = await api.get(
      `/payroll/attendance-summary/${employeeId}?business_id=${businessId}&month=${month}&year=${year}&gross_salary=${grossSalary}`
    );
    return res.data;
  }, [businessId]);

  const downloadPayslipPDF = async (payrollId) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${BACKEND_URL}/api/reports/payslip/${payrollId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error("Failed to download payslip");
    return await res.blob();
  };

  const exportPayroll = async (startMonth, endMonth, format = 'excel', employeeId = null, status = null) => {
    let url = `/payroll/export/${format}?business_id=${businessId}&start_month=${startMonth}&end_month=${endMonth}`;
    if (employeeId && employeeId !== 'all') url += `&employee_id=${employeeId}`;
    if (status && status !== 'all') url += `&status=${status}`;
    
    const res = await api.get(url, { responseType: 'blob' });
    return res.data;
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  // Stats
  const pendingPayrolls = payrolls.filter(p => p.status === 'pending');
  const processedPayrolls = payrolls.filter(p => p.status === 'processed');
  
  const stats = {
    total: payrolls.length,
    pending: pendingPayrolls.length,
    processed: processedPayrolls.length,
    totalPending: pendingPayrolls.reduce((sum, p) => sum + (p.net_salary || 0), 0),
    totalProcessed: processedPayrolls.reduce((sum, p) => sum + (p.net_salary || 0), 0),
  };

  return {
    payrolls,
    employees,
    loading,
    error,
    stats,
    fetchData,
    createPayroll,
    generateBulkPayroll,
    processPayroll,
    autoCalculate,
    manualCalculate,
    fetchPendingAdvances,
    fetchAttendanceSummary,
    downloadPayslipPDF,
    exportPayroll,
    getEmployeeName,
  };
};

/**
 * Hook for payroll configuration
 */
export const usePayrollConfig = (businessId) => {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.get(`/payroll/config?business_id=${businessId}`);
      setConfig(res.data);
    } catch (err) {
      console.error('Error fetching payroll config:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateConfig = async (updates) => {
    const res = await api.put(`/payroll/config?business_id=${businessId}`, updates);
    setConfig(res.data);
    return res.data;
  };

  return { config, loading, fetchConfig, updateConfig };
};
